import React, { useEffect, useMemo, useRef } from 'react';
import './MotionTimeline.css';
import {
  timeToX,
  xToTime,
  frameAtTime,
  segmentAtFrame,
  buildEnvelope,
  captureGapMarkerXs,
  niceTimeTicks,
  formatTime,
} from '../utils/motionTimeline';
import { trackEvent } from '../analytics';
import useVideoScrub from '../hooks/useVideoScrub';

// Invariant: React re-renders ONLY on prop change. Everything driven by
// playback time — playhead, time text, motion readout, kept/discarded pill,
// aria — is mutated imperatively via refs so a 60Hz rAF loop never triggers
// a component render.

const TRACK_HEIGHT = 150;
const TICK_BAND = 18;
const TOP_PAD = 6;

const KEPT_FILL = 'rgba(0,179,166,0.10)';
const KEPT_EDGE = 'rgba(0,179,166,0.35)';
const CURVE_TEAL = '#00b3a6';
const AREA_TEAL = 'rgba(0,179,166,0.28)';
const CURVE_GRAY = '#5a6572';
const AREA_GRAY = 'rgba(90,101,114,0.22)';
const PANEL_BG = '#16181d';
const THRESHOLD_LINE = 'rgba(255,255,255,0.4)';
const THRESHOLD_LABEL = 'rgba(255,255,255,0.45)';
const TICK_LINE = 'rgba(255,255,255,0.12)';
const HOVER_LINE = 'rgba(255,255,255,0.35)';
const PLAYHEAD = '#ff4d4d';
// Capture gaps (camera off overnight / over a weekend). Muted amber so the
// marker reads as an annotation, not as data: it must not compete with the
// teal segment bands, the white threshold line, or the red playhead.
const CAPTURE_GAP_LINE = 'rgba(226,178,96,0.5)';

const MotionTimeline = ({
  motionValues,
  motionSegments,
  motionThreshold,
  captureGapFrames,
  fps,
  videoRef,
  videoUrl,
  isRecalculating,
  projectId,
}) => {
  const trackRef = useRef(null);
  const staticCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeRef = useRef(null);
  const motionValueRef = useRef(null);
  const keptPillRef = useRef(null);

  const sizeRef = useRef({ w: 0, h: TRACK_HEIGHT, dpr: 1 });
  const scaleRef = useRef({ yMax: 1, plotTop: 0, plotBottom: 0 });
  const envelopeCacheRef = useRef({ values: null, width: 0, env: null });
  const hoverXRef = useRef(null);
  const rafRef = useRef(0);
  const playingRef = useRef(false);

  // The resize and video-listener effects below intentionally have narrow deps,
  // so they must draw through these always-current refs — calling the captured
  // closures directly would paint stale segments/threshold after a
  // recalculation. Declared up here because the scrub hook repaints through
  // them too.
  const drawStaticRef = useRef(null);
  const drawOverlayRef = useRef(null);

  const safeValues = useMemo(
    () => (Array.isArray(motionValues) ? motionValues : []),
    [motionValues]
  );
  const safeSegments = useMemo(
    () => (Array.isArray(motionSegments) ? motionSegments : []),
    [motionSegments]
  );
  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : 0;
  const frameCount = safeValues.length;
  const domain = safeFps > 0 ? frameCount / safeFps : 0;

  // Seek-when-ready, fastSeek-while-down, and the display-time precedence the
  // playhead is drawn from — shared verbatim with the pace chart.
  const scrub = useVideoScrub(videoRef, {
    domain,
    onUpdate: () => {
      if (drawOverlayRef.current) drawOverlayRef.current();
    },
  });

  const gapFrames = useMemo(
    () => (Array.isArray(captureGapFrames) ? captureGapFrames : []),
    [captureGapFrames]
  );
  // Whether any gap frame is actually plottable — the legend entry appears only
  // then. Width 1 because only the count matters here; the marker positions
  // themselves are computed at draw time, against the live canvas width.
  const hasCaptureGaps = useMemo(
    () => captureGapMarkerXs(gapFrames, frameCount, 1).length > 0,
    [gapFrames, frameCount]
  );

  const yMax = useMemo(() => {
    if (safeValues.length === 0) return 1;
    let m = -Infinity;
    for (let i = 0; i < safeValues.length; i++) {
      const v = safeValues[i];
      if (Number.isFinite(v) && v > m) m = v;
    }
    return m > 0 ? m : 1;
  }, [safeValues]);

  const valueToY = (v, plotTop, plotBottom) => {
    const range = plotBottom - plotTop;
    const clamped = Math.max(0, Math.min(v, yMax));
    return plotBottom - (clamped / yMax) * range;
  };

  const drawStatic = () => {
    const canvas = staticCanvasRef.current;
    if (!canvas) return;
    const { w, h, dpr } = sizeRef.current;
    if (w <= 0 || h <= 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    ctx.fillStyle = PANEL_BG;
    ctx.fillRect(0, 0, w, h);

    const plotTop = TOP_PAD;
    const plotBottom = h - TICK_BAND;
    scaleRef.current = { yMax, plotTop, plotBottom };

    // Kept segments band + edges.
    if (domain > 0 && safeSegments.length > 0) {
      for (const seg of safeSegments) {
        const t0 = seg.start / safeFps;
        const t1 = seg.end / safeFps;
        const x0 = timeToX(t0, domain, w);
        const x1 = timeToX(t1, domain, w);
        const bw = Math.max(0, x1 - x0);
        ctx.fillStyle = KEPT_FILL;
        ctx.fillRect(x0, plotTop, bw, plotBottom - plotTop);
        ctx.strokeStyle = KEPT_EDGE;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x0 + 0.5, plotTop);
        ctx.lineTo(x0 + 0.5, plotBottom);
        ctx.moveTo(x1 - 0.5, plotTop);
        ctx.lineTo(x1 - 0.5, plotBottom);
        ctx.stroke();
      }
    }

    // Curve: gray everywhere, then teal clipped to kept segments.
    if (frameCount > 0 && domain > 0) {
      drawCurve(ctx, w, plotTop, plotBottom, CURVE_GRAY, AREA_GRAY);

      if (safeSegments.length > 0) {
        ctx.save();
        ctx.beginPath();
        for (const seg of safeSegments) {
          const t0 = seg.start / safeFps;
          const t1 = seg.end / safeFps;
          const x0 = timeToX(t0, domain, w);
          const x1 = timeToX(t1, domain, w);
          ctx.rect(x0, plotTop, Math.max(0, x1 - x0), plotBottom - plotTop);
        }
        ctx.clip();
        drawCurve(ctx, w, plotTop, plotBottom, CURVE_TEAL, AREA_TEAL);
        ctx.restore();
      }
    }

    // Capture-gap markers: one thin dashed vertical line per camera-off
    // stretch, on the boundary between the pairs it separates. Drawn over the
    // curve (the masked pair reads as a zero there) but under the threshold
    // line and the overlay, so it annotates without dominating.
    if (frameCount > 0) {
      const xs = captureGapMarkerXs(gapFrames, frameCount, w);
      if (xs.length > 0) {
        ctx.save();
        ctx.strokeStyle = CAPTURE_GAP_LINE;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        for (const x of xs) {
          const px = Math.round(x) + 0.5;
          ctx.moveTo(px, plotTop);
          ctx.lineTo(px, plotBottom);
        }
        ctx.stroke();
        ctx.restore();
      }
    }

    // Threshold line (clamp only within ~10% overshoot; otherwise skip).
    if (Number.isFinite(motionThreshold) && motionThreshold >= 0) {
      let effective = motionThreshold;
      if (motionThreshold > yMax) {
        if (motionThreshold <= yMax * 1.1) effective = yMax;
        else effective = null;
      }
      if (effective !== null) {
        const y = valueToY(effective, plotTop, plotBottom);
        ctx.save();
        ctx.strokeStyle = THRESHOLD_LINE;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(0, y + 0.5);
        ctx.lineTo(w, y + 0.5);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = THRESHOLD_LABEL;
        ctx.font = '9px system-ui, sans-serif';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillText('threshold', w - 4, y - 2);
        ctx.restore();
      }
    }

    // Time ticks.
    if (domain > 0) {
      const maxTicks = Math.max(2, Math.floor(w / 80));
      const ticks = niceTimeTicks(domain, maxTicks);
      ctx.save();
      ctx.strokeStyle = TICK_LINE;
      ctx.lineWidth = 1;
      ctx.fillStyle = THRESHOLD_LABEL;
      ctx.font = '10px system-ui, sans-serif';
      ctx.textBaseline = 'top';
      for (const t of ticks) {
        const x = timeToX(t, domain, w);
        ctx.beginPath();
        ctx.moveTo(x + 0.5, plotBottom);
        ctx.lineTo(x + 0.5, h);
        ctx.stroke();
        const label = formatTime(t);
        const textW = ctx.measureText(label).width;
        let lx = x - textW / 2;
        if (lx < 2) lx = 2;
        if (lx + textW > w - 2) lx = w - 2 - textW;
        ctx.textAlign = 'left';
        ctx.fillText(label, lx, plotBottom + 4);
      }
      ctx.restore();
    }
  };

  const drawCurve = (ctx, w, plotTop, plotBottom, stroke, fill) => {
    if (frameCount === 0) return;
    const useEnvelope = frameCount >= 2 * w && w > 0;
    ctx.save();
    if (useEnvelope) {
      let env = envelopeCacheRef.current.env;
      if (
        envelopeCacheRef.current.values !== safeValues ||
        envelopeCacheRef.current.width !== w ||
        !env
      ) {
        env = buildEnvelope(safeValues, w);
        envelopeCacheRef.current = { values: safeValues, width: w, env };
      }
      // Area fill down to baseline
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.moveTo(0, plotBottom);
      for (let i = 0; i < env.length; i++) {
        const yTop = valueToY(env[i].max, plotTop, plotBottom);
        ctx.lineTo(i, yTop);
      }
      ctx.lineTo(env.length - 1, plotBottom);
      ctx.closePath();
      ctx.fill();

      // Vertical min-max band per column
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i < env.length; i++) {
        const yMin = valueToY(env[i].min, plotTop, plotBottom);
        const yTop = valueToY(env[i].max, plotTop, plotBottom);
        ctx.moveTo(i + 0.5, yTop);
        ctx.lineTo(i + 0.5, yMin);
      }
      ctx.stroke();
    } else {
      const step = w / Math.max(1, frameCount - 1);
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.moveTo(0, plotBottom);
      for (let i = 0; i < frameCount; i++) {
        const x = i * step;
        const y = valueToY(safeValues[i], plotTop, plotBottom);
        ctx.lineTo(x, y);
      }
      ctx.lineTo((frameCount - 1) * step, plotBottom);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i < frameCount; i++) {
        const x = i * step;
        const y = valueToY(safeValues[i], plotTop, plotBottom);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();
  };

  const drawOverlay = () => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const { w, h, dpr } = sizeRef.current;
    if (w <= 0 || h <= 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const { plotTop, plotBottom } = scaleRef.current;
    const scrubbing = scrub.isScrubbing();
    // Prefers a not-yet-applied seek target over video.currentTime so the
    // playhead never snaps back while the video is still seeking.
    const displayTime = scrub.getDisplayTime();

    // Hover line
    const hx = hoverXRef.current;
    if (hx != null && !scrubbing) {
      ctx.save();
      ctx.strokeStyle = HOVER_LINE;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(hx + 0.5, plotTop);
      ctx.lineTo(hx + 0.5, plotBottom);
      ctx.stroke();
      ctx.restore();
    }

    // Playhead
    if (domain > 0 || frameCount > 0) {
      const px = timeToX(displayTime, domain, w);
      ctx.save();
      ctx.strokeStyle = PLAYHEAD;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, plotTop);
      ctx.lineTo(px, plotBottom);
      ctx.stroke();

      // Grab dot at top
      ctx.fillStyle = PLAYHEAD;
      ctx.beginPath();
      ctx.arc(px, plotTop, 5, 0, Math.PI * 2);
      ctx.fill();

      // Dot on the curve
      const f = frameAtTime(displayTime, safeFps, frameCount);
      if (f >= 0 && f < frameCount) {
        const v = safeValues[f];
        if (Number.isFinite(v)) {
          const y = valueToY(v, plotTop, plotBottom);
          ctx.beginPath();
          ctx.arc(px, y, 3.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    }

    // DOM readouts
    if (timeRef.current) {
      timeRef.current.textContent = formatTime(displayTime);
    }
    if (motionValueRef.current) {
      if (frameCount === 0) {
        motionValueRef.current.textContent = '–';
      } else {
        const f = frameAtTime(displayTime, safeFps, frameCount);
        const v = f >= 0 ? safeValues[f] : null;
        if (Number.isFinite(v)) {
          motionValueRef.current.textContent = `Motion ${Math.round(v).toLocaleString()}`;
        } else {
          motionValueRef.current.textContent = '–';
        }
      }
    }
    if (keptPillRef.current) {
      const f = frameAtTime(displayTime, safeFps, frameCount);
      const kept = f >= 0 ? segmentAtFrame(f, safeSegments) !== null : false;
      keptPillRef.current.textContent = kept ? 'Kept' : 'Discarded';
      keptPillRef.current.className = kept
        ? 'motion-timeline__pill motion-timeline__pill--kept'
        : 'motion-timeline__pill motion-timeline__pill--discarded';
    }
    if (trackRef.current) {
      const seconds = Math.round(displayTime);
      trackRef.current.setAttribute('aria-valuenow', String(seconds));
      trackRef.current.setAttribute(
        'aria-valuetext',
        `${formatTime(displayTime)} of ${formatTime(domain)}`
      );
    }
  };

  drawStaticRef.current = drawStatic;
  drawOverlayRef.current = drawOverlay;

  // Resize + initial draw
  useEffect(() => {
    const wrap = trackRef.current;
    if (!wrap) return undefined;
    const applySize = () => {
      const rect = wrap.getBoundingClientRect();
      const w = Math.max(0, Math.floor(rect.width));
      const h = TRACK_HEIGHT;
      const dpr = window.devicePixelRatio || 1;
      sizeRef.current = { w, h, dpr };
      for (const canvas of [staticCanvasRef.current, overlayCanvasRef.current]) {
        if (!canvas) continue;
        canvas.width = Math.max(1, Math.floor(w * dpr));
        canvas.height = Math.max(1, Math.floor(h * dpr));
      }
      envelopeCacheRef.current = { values: null, width: 0, env: null };
      drawStaticRef.current();
      drawOverlayRef.current();
    };
    applySize();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(applySize);
      ro.observe(wrap);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', applySize);
    return () => window.removeEventListener('resize', applySize);
  }, []);

  // Redraw static layer whenever data-driven props change.
  useEffect(() => {
    drawStatic();
    drawOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeValues, safeSegments, motionThreshold, gapFrames, safeFps, yMax]);

  // Re-apply the imperatively mutated readout/aria DOM state after every
  // commit, so it survives any render that rewrites those nodes (e.g. a
  // domain change updating aria-valuetext from JSX).
  useEffect(() => {
    drawOverlayRef.current();
  });

  // Video playback wiring.
  useEffect(() => {
    const video = videoRef && videoRef.current;
    if (!video) {
      drawOverlayRef.current();
      return undefined;
    }

    const tick = () => {
      drawOverlayRef.current();
      if (playingRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    const onPlay = () => {
      playingRef.current = true;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };
    const onStop = () => {
      playingRef.current = false;
      cancelAnimationFrame(rafRef.current);
      drawOverlayRef.current();
    };
    const onTime = () => drawOverlayRef.current();
    const onSeeked = () => {
      // seek-when-ready pattern: apply any queued seek (and, once the chain has
      // settled outside a drag, drop the stale target). `scrub` is a stable
      // object, so this closure never goes out of date.
      scrub.onSeeked();
      drawOverlayRef.current();
    };
    const onLoaded = () => drawOverlayRef.current();

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onStop);
    video.addEventListener('ended', onStop);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('loadedmetadata', onLoaded);

    // The timeline can mount mid-playback (expanding the Trim card while the
    // preview above is already playing), and the `play` event fired long ago —
    // so seed the loop from the element's state instead of waiting for it.
    playingRef.current = !video.paused && !video.ended;
    if (playingRef.current) {
      rafRef.current = requestAnimationFrame(tick);
    }

    drawOverlayRef.current();

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onStop);
      video.removeEventListener('ended', onStop);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('loadedmetadata', onLoaded);
      cancelAnimationFrame(rafRef.current);
      playingRef.current = false;
    };
    // [videoUrl] is sufficient: VideoPlayer keys the <video> element by src
    // (key={src}), so the element's identity can only change when videoUrl
    // does; any other remount unmounts this component too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  const localX = (clientX) => {
    const wrap = trackRef.current;
    if (!wrap) return 0;
    const rect = wrap.getBoundingClientRect();
    return clientX - rect.left;
  };

  const updateTooltip = (x, t) => {
    const el = tooltipRef.current;
    const wrap = trackRef.current;
    if (!el || !wrap) return;
    const f = frameAtTime(t, safeFps, frameCount);
    const kept = f >= 0 ? segmentAtFrame(f, safeSegments) !== null : false;
    const v = f >= 0 ? safeValues[f] : null;
    const parts = [formatTime(t)];
    if (Number.isFinite(v)) {
      parts.push(`Motion ${Math.round(v).toLocaleString()}`);
    }
    parts.push(kept ? 'Kept' : 'Discarded');
    el.textContent = parts.join(' · ');
    el.style.display = 'block';

    const rect = wrap.getBoundingClientRect();
    const flipRight = x > rect.width - 100;
    if (flipRight) {
      el.style.right = `${Math.max(4, rect.width - x + 8)}px`;
      el.style.left = 'auto';
    } else {
      el.style.left = `${Math.max(4, x + 8)}px`;
      el.style.right = 'auto';
    }
  };

  const hideTooltip = () => {
    const el = tooltipRef.current;
    if (el) el.style.display = 'none';
  };

  const onPointerDown = (e) => {
    if (!(domain > 0)) return;
    const wrap = trackRef.current;
    if (!wrap) return;
    try {
      wrap.setPointerCapture(e.pointerId);
    } catch (err) {
      // ignore
    }
    scrub.beginScrub();
    const x = localX(e.clientX);
    const t = xToTime(x, domain, sizeRef.current.w);
    scrub.seekTo(t);
    hideTooltip();
  };

  const onPointerMove = (e) => {
    const x = localX(e.clientX);
    if (scrub.isScrubbing()) {
      const t = xToTime(x, domain, sizeRef.current.w);
      scrub.seekTo(t);
      return;
    }
    if (!(domain > 0)) return;
    hoverXRef.current = x;
    const t = xToTime(x, domain, sizeRef.current.w);
    updateTooltip(x, t);
    drawOverlay();
  };

  const endScrub = (e) => {
    if (!scrub.isScrubbing()) return;
    trackEvent('timeline_scrubbed', { project_id: projectId });
    const wrap = trackRef.current;
    if (wrap) {
      try {
        wrap.releasePointerCapture(e.pointerId);
      } catch (err) {
        // ignore
      }
    }
    const x = localX(e.clientX);
    const t = xToTime(x, domain, sizeRef.current.w);
    // Clears the scrub flag before the final seek, so the release lands as one
    // precise currentTime write rather than another fastSeek.
    scrub.endScrub(t);
    hoverXRef.current = null;
    drawOverlay();
  };

  const onPointerLeave = () => {
    if (scrub.isScrubbing()) return;
    hoverXRef.current = null;
    hideTooltip();
    drawOverlay();
  };

  const onKeyDown = (e) => {
    if (!(domain > 0)) return;
    // While a seek is in flight, video.currentTime still reports the old
    // position; step from the last requested target so rapid/held arrow keys
    // accumulate instead of recomputing the same destination.
    const cur = scrub.getStepBaseTime();
    let target = null;
    const bigStep = e.shiftKey ? 5 : 1;
    switch (e.key) {
      case 'ArrowLeft':
        target = cur - bigStep;
        break;
      case 'ArrowRight':
        target = cur + bigStep;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = domain;
        break;
      default:
        return;
    }
    e.preventDefault();
    scrub.seekTo(target);
    drawOverlay();
  };

  const domainRounded = Math.round(domain);

  return (
    <div className="motion-timeline">
      <div
        className="motion-timeline__canvas-wrap"
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label="Video timeline"
        aria-valuemin={0}
        aria-valuemax={domainRounded}
        aria-valuenow={0}
        aria-valuetext={`0:00 of ${formatTime(domain)}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endScrub}
        onPointerCancel={endScrub}
        onPointerLeave={onPointerLeave}
        onKeyDown={onKeyDown}
      >
        <canvas ref={staticCanvasRef} className="motion-timeline__canvas" />
        <canvas ref={overlayCanvasRef} className="motion-timeline__canvas" />
        <div className="motion-timeline__tooltip" ref={tooltipRef} />
      </div>
      <div className="motion-timeline__readout">
        <span className="motion-timeline__time">
          <span ref={timeRef}>0:00</span> / {formatTime(domain)}
        </span>
        <span className="motion-timeline__frame-info">
          <span ref={motionValueRef} className="motion-timeline__motion-value">–</span>
          <span
            ref={keptPillRef}
            className="motion-timeline__pill motion-timeline__pill--discarded"
          >
            Discarded
          </span>
        </span>
        <span className="motion-timeline__legend">
          <span className="motion-timeline__swatch motion-timeline__swatch--kept" />
          Kept
          <span className="motion-timeline__swatch motion-timeline__swatch--discarded" />
          Discarded
          <span className="motion-timeline__swatch motion-timeline__swatch--threshold" />
          Threshold
          {hasCaptureGaps && (
            <>
              <span className="motion-timeline__swatch motion-timeline__swatch--capture-gap" />
              Capture gap
            </>
          )}
        </span>
      </div>
      {isRecalculating && (
        <div className="motion-timeline-overlay" role="status" aria-live="polite">
          <div className="motion-timeline-spinner" />
          <span className="motion-timeline-overlay-text">Recalculating...</span>
        </div>
      )}
    </div>
  );
};

export default MotionTimeline;
